export const MeetingRecordStatus = {
  PROCESSING: 'processing',
  DRAFT: 'draft',
  COMPLETE: 'complete',
  FAILED: 'failed',
} as const;

export type TMeetingRecordStatus = typeof MeetingRecordStatus[keyof typeof MeetingRecordStatus];

export const ProcessingStages = {
  UPLOADING: 'uploading',
  TRANSCRIBING: 'transcribing',
  ANALYZING: 'analyzing',
  FINALIZING: 'finalizing',
  TEXT_ANALYSIS: 'textAnalysis',
  AUDIO_ANALYSIS: 'audioAnalysis',
  SUMMARIZATION: 'summarization',
} as const;

export type TProcessingStage = typeof ProcessingStages[keyof typeof ProcessingStages];

export const ProcessingStageSteps: Record<TProcessingStage, number> = {
  [ProcessingStages.UPLOADING]: 1,
  [ProcessingStages.TRANSCRIBING]: 2,
  [ProcessingStages.ANALYZING]: 3,
  [ProcessingStages.TEXT_ANALYSIS]: 3,
  [ProcessingStages.AUDIO_ANALYSIS]: 3,
  [ProcessingStages.SUMMARIZATION]: 3,
  [ProcessingStages.FINALIZING]: 4,
};

export type TMeetingRecord = {
  audioAnalyze?: string;
  date: string;
  meetingSummary: string;
  optionsForReport: string;
  recordNumber: number;
  textAnalyze?: string;
  therapistId: number;
  therapistName?: string;
  transcription: string;
  type: string;
  userId: number;
  videoAnalyze?: string;
  duration?: number | null;
  status?: TMeetingRecordStatus;
  time?: string | null;
  subject?: string | null;
  completedAt?: string | null;
  processingStage?: TProcessingStage | null;
  errorMessage?: string | null;
  retryCount?: number;
  audioFileUrl?: string | null;
  failedAtStage?: TProcessingStage | null;
};

export type TGetUserMeetingRecordsResponse = {
  records: TMeetingRecord[];
  count: number;
};

// Text Analysis Types
export interface TTextAnalysisStatistics {
  word_count: number;
  sentence_count: number;
  medical_terms_count: number;
  readability_score: number;
}

export interface TTextAnalysisTopic {
  title: string;
  description: string;
  icon?: string;
}

export interface TTextAnalysisInsight {
  title: string;
  text: string;
  icon?: string;
}

export interface TTextAnalysisRiskAssessment {
  level: "None" | "Low" | "Moderate" | "High";
  justification: string;
}

export interface TTextAnalysisSentiment {
  overall: { positive: number; negative: number; };
  sadness_percent: number;
  fear_percent: number;
  anger_percent: number;
  joy_percent: number;
  happiness_percent: number;
  anticipation_percent: number;
  disgust_percent: number;
  surprise_percent: number;
  other_percent: number;
}

export interface TTextAnalysis {
  statistics?: TTextAnalysisStatistics;
  keywords?: string[];
  main_topics?: TTextAnalysisTopic[];
  insights?: TTextAnalysisInsight[];
  clinical_flags?: string[];
  key_observations?: string[];
  linguistic_patterns?: string[];
  suggested_clinical_track?: string;
  risk_assessment?: TTextAnalysisRiskAssessment;
  scope_note?: string;
  sentiment_analysis?: TTextAnalysisSentiment;
}

// Structured Meeting Summary Types
export interface TPatientStatus {
  currentCondition?: string;
  progressNotes?: string;
  concerns?: string;
}

export interface TTreatmentPlan {
  current?: string;
  modifications?: string;
  nextSteps?: string;
}

export interface TStructuredMeetingSummary {
  subject: string;
  primaryFindings: string[];
  decisions: string[];
  recommendations: string[];
  actionItems: string[];
  patientStatus: TPatientStatus;
  treatmentPlan: TTreatmentPlan;
  additionalNotes: string;
  nextAppointment: string;
}

